import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Button from 'components/form/Button'
import { PresetListItem } from 'features/dashboard/table/config/PresetListItem'
import {
  CustomPreset,
  DomainDefaultColumns,
  getEtcPresets,
  getGeneralPresets,
  MetricPresets,
  PresetOrders,
} from 'features/dashboard/table/shared/Presets'
import { arrayEquals, isPresent } from 'utils'
import { UserPresetListItem } from 'features/dashboard/table/config/UserPresetListItem'
import {
  applyDefaultPreset,
  clearError,
  deleteUserPresets,
  fetchUserPresets,
  hidePresetConfigModal,
  showPresetConfigModal,
} from 'features/dashboard/table/config/tableConfigSlice'
import PresetConfigModal from 'features/dashboard/table/config/PresetConfigModal'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import { useErrorToast } from 'utils/hooks/useErrorToast'
import { useConfirm } from 'features/dialog/Confirm'
import { success } from 'features/toast/Toast'
import NoneAnchor from 'components/NoneAnchor'
import { useDashboardTypeName } from 'features/dashboard/utils/hooks/useDashboardTypeName'
import useOutsideClick from '../../../../utils/hooks/useOutsideClick'

const ColumnSelect = () => {
  const dispatch = useDispatch()
  const { type: domainType } = useDashboardTypeName()
  const [isOpened, setOpened] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [idToDelete, setIdToDelete] = useState()
  const [PresetDeleteConfirm, showPresetDeleteConfirm] = useConfirm()

  const {
    columns,
    appliedColumnPresets,
    selectedUserPresetId,
    userPresets,
    isLoaded,
    loading,
    error,
  } = useSelector(
    ({ dashboard }) => ({
      columns: dashboard.table.config.columns[domainType],
      appliedColumnPresets:
        dashboard.table.config.appliedColumnPresets[domainType],
      selectedUserPresetId:
        dashboard.table.config.selectedUserPresetId[domainType],
      userPresets: dashboard.table.config.userPresets[domainType],
      isLoaded: dashboard.table.config.isLoaded[domainType],
      loading: dashboard.table.config.loading[domainType],
      error: dashboard.table.config.error[domainType],
    }),
    shallowEqual
  )

  const generalPreset = getGeneralPresets(domainType)
  const etcPreset = getEtcPresets(domainType)

  useErrorToast({
    error,
    handleClearError: () => dispatch(clearError(domainType)),
  })

  useEffect(() => {
    if (!isLoaded) {
      dispatch(fetchUserPresets(domainType))
    }
  }, [dispatch, isLoaded, userPresets, domainType])

  useEffect(
    () => () => {
      dispatch(hidePresetConfigModal(domainType))
    },
    [dispatch, domainType]
  )

  const isDefaultPreset = useMemo(
    () => arrayEquals(columns, DomainDefaultColumns[domainType]),
    [columns, domainType]
  )

  const selectedPresetsAsText = useMemo(
    () =>
      isPresent(selectedUserPresetId)
        ? isLoaded &&
          (userPresets.find(({ id }) => id === selectedUserPresetId)?.name ||
            '맞춤 설정')
        : isDefaultPreset
        ? '기본 설정'
        : columns.length === 0 || appliedColumnPresets[CustomPreset.name]
        ? CustomPreset.displayName
        : PresetOrders.map(key => {
            const checked = appliedColumnPresets[key]
            if (checked) {
              if (key === 'GENERAL') {
                return getGeneralPresets(domainType).displayName
              } else if (key === 'ETC') {
                return getEtcPresets(domainType).displayName
              } else {
                return MetricPresets[key].displayName
              }
            } else {
              return undefined
            }
          })
            .filter(Boolean)
            .join(', '),
    [
      appliedColumnPresets,
      userPresets,
      selectedUserPresetId,
      isDefaultPreset,
      domainType,
      columns,
      isLoaded,
    ]
  )

  const deletePreset = async () => {
    if (isPresent(idToDelete)) {
      await dispatch(
        deleteUserPresets({
          id: idToDelete,
          domainType,
        })
      )
      if (idToDelete === selectedUserPresetId) {
        dispatch(applyDefaultPreset(domainType))
      }
      setIdToDelete(undefined)
      setOpened(false)
      success('변경사항이 적용되었습니다.')
    }
  }
  const ref = useOutsideClick(e => {
    if (
      document.getElementById('root').contains(e.target) ||
      e.target === document.body
    )
      setOpened(false)
  })
  return (
    <>
      <PresetConfigModal domainType={domainType} />

      <PresetDeleteConfirm
        title="열 맞춤 설정 삭제"
        onClose={({ isConfirmed }) => isConfirmed && deletePreset()}>
        열 맞춤 설정을 삭제하시겠습니까?
      </PresetDeleteConfirm>

      <div
        ref={ref}
        className={classNames('opt_select opt_right', {
          opt_open: isOpened,
        })}>
        <div className="screen_out">데이터종류 선택상자</div>
        <span className="screen_out">선택내용 : {selectedPresetsAsText}</span>
        <NoneAnchor
          className="link_selected"
          onClick={() => setOpened(_isOpened => !_isOpened)}>
          열 : {selectedPresetsAsText}
        </NoneAnchor>
        <span className="ico_arr" />
        <div className="screen_out">선택옵션</div>
        <div className="optlist_row" style={{ zIndex: 21 }}>
          <ul className="list_optrow">
            <li
              className={classNames('user_opt', { on: previewVisible })}
              onMouseEnter={() => setPreviewVisible(true)}
              onMouseLeave={() => setPreviewVisible(false)}>
              <NoneAnchor
                className="link_option"
                onClick={() => {
                  dispatch(applyDefaultPreset(domainType))
                  setOpened(false)
                }}>
                기본 설정
              </NoneAnchor>
            </li>
            <li className={classNames('user_opt', { on: !previewVisible })}>
              <NoneAnchor className="link_option">사용자 맞춤 설정</NoneAnchor>
            </li>
          </ul>
          <div className="optrow_wrap">
            {previewVisible ? (
              <p className="desc_nodata">
                비용지표, 기본지표 등<br />
                시스템 내 기본 설정 값으로
                <br />
                보실 수 있습니다.
              </p>
            ) : (
              <>
                <div className="optrow_cell">
                  <div className="list_opt2">
                    <strong className="tit_option">정보</strong>
                    <ul className="list_opt sub_listopt">
                      <PresetListItem preset={generalPreset} />
                    </ul>
                    <strong className="tit_option">실적 지표</strong>
                    <ul className="list_opt sub_listopt">
                      <PresetListItem preset={MetricPresets.BASIC} />
                      <PresetListItem preset={MetricPresets.ADDITIONAL} />
                    </ul>
                    <strong className="tit_option">전환 지표</strong>
                    <ul className="list_opt sub_listopt">
                      <PresetListItem preset={MetricPresets.PIXEL_AND_SDK} />
                      <PresetListItem preset={MetricPresets.TALK_CHANNEL} />
                    </ul>
                    <strong className="tit_option">기타</strong>
                    <ul className="list_opt sub_listopt">
                      <PresetListItem preset={etcPreset} />
                    </ul>
                  </div>
                </div>
                <div className="optrow_cell">
                  <LoadingWrapper isLoading={loading}>
                    <div className="list_opt2">
                      <strong className="tit_option">사용자 맞춤 지표</strong>
                      {isPresent(userPresets) ? (
                        <div className="scroll_listopt">
                          <ul className="list_opt sub_listopt">
                            {userPresets.map(userPreset => (
                              <UserPresetListItem
                                userPreset={userPreset}
                                key={userPreset.id}
                                handleListClose={() => setOpened(false)}
                                handleDelete={id => {
                                  setIdToDelete(id)
                                  showPresetDeleteConfirm()
                                }}
                              />
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="desc_none">
                          {loading || (
                            <>
                              사용자 맞춤 지표를
                              <br />
                              설정하세요
                            </>
                          )}
                        </div>
                      )}
                      <Button
                        className="btn_gm gm_line btn_colset"
                        onClick={() => {
                          dispatch(showPresetConfigModal({ domainType }))
                          setOpened(false)
                        }}>
                        열 맞춤 설정
                      </Button>
                    </div>
                  </LoadingWrapper>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ColumnSelect
